import { reportInfo } from "@/lib/observability/report-error";

export type NativeAppEnvironment = "prod" | "stage";

type NativeCallbackKind = "auth-callback" | "oauth-callback";

const STAGE_HOSTS = new Set(["stagev2.vidseeds.ai"]);
/** Apex + www both serve production Digital Asset Links / AASA. */
const PROD_HOSTS = new Set(["vidseeds.ai", "www.vidseeds.ai"]);

function extractHost(candidate: string | null | undefined): string | null {
  if (!candidate || candidate.trim() === "") return null;

  try {
    const url = candidate.includes("://") ? candidate : `https://${candidate}`;
    return new URL(url).hostname.toLowerCase();
  } catch (error) {
    reportInfo(error, { source: "native-callbacks.extractHost" });
    return (
      candidate
        .trim()
        .replace(/^[a-z]+:\/\//i, "")
        .split("/")[0]
        ?.split(":")[0]
        ?.toLowerCase() || null
    );
  }
}

function buildNativeCallbackUrl(
  scheme: string,
  callbackKind: NativeCallbackKind
): string {
  return `${scheme}://${callbackKind}`;
}

export function resolveNativeAppEnvironment(
  hostOrUrl?: string | null
): NativeAppEnvironment {
  const host = extractHost(hostOrUrl);

  if (host && STAGE_HOSTS.has(host)) {
    return "stage";
  }

  if (host && PROD_HOSTS.has(host)) {
    return "prod";
  }

  return "prod";
}

export function getNativeCallbackSchemeForEnvironment(
  environment: NativeAppEnvironment
): string {
  return environment === "stage" ? "vidseeds-stage" : "vidseeds-prod";
}

export function getNativeAuthCallbackUrlForEnvironment(
  environment: NativeAppEnvironment
): string {
  return buildNativeCallbackUrl(
    getNativeCallbackSchemeForEnvironment(environment),
    "auth-callback"
  );
}

export function getNativePlatformCallbackUrlForEnvironment(
  environment: NativeAppEnvironment
): string {
  return buildNativeCallbackUrl(
    getNativeCallbackSchemeForEnvironment(environment),
    "oauth-callback"
  );
}

export function isKnownNativeAuthCallbackUrl(url: string): boolean {
  return ["prod", "stage"].some((environment) =>
    url.startsWith(
      getNativeAuthCallbackUrlForEnvironment(
        environment as NativeAppEnvironment
      )
    )
  );
}

export function isKnownNativePlatformCallbackUrl(url: string): boolean {
  return ["prod", "stage"].some((environment) =>
    url.startsWith(
      getNativePlatformCallbackUrlForEnvironment(
        environment as NativeAppEnvironment
      )
    )
  );
}
